import { 
  PlayIcon, 
  DocumentTextIcon, 
  StarIcon, 
  ClockIcon, 
  UserGroupIcon, 
  ArrowTopRightOnSquareIcon 
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useDocsPosts, useDocsFeaturedImage } from '@/hooks/useWordPressDocs';
import { useNavigate } from 'react-router-dom';
import { docsService } from '@/services/docsService';

const PopularResources = () => {
  const navigate = useNavigate();
  // Try to fetch docs posts specifically, fall back to regular posts if needed
  const { data: postsData, isLoading } = useDocsPosts({ 
    per_page: 6, 
    orderby: 'date',
    order: 'desc',
    doc_category: '1' // Start with category 1 to test
  });

  const handlePostClick = (post: any) => {
    navigate(`/support/article/${post.slug}`);
  };

  const estimateReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = docsService.stripHtml(content).split(' ').length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  if (isLoading) {
    return (
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Loading <span className="text-primary">Helpful Resources</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader className="space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-6 bg-muted rounded w-full"></div>
                  <div className="h-4 bg-muted rounded w-5/6"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-muted rounded w-full"></div>
                    <div className="h-3 bg-muted rounded w-3/4"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Most <span className="text-primary">Helpful Resources</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            These are the resources our community finds most valuable. 
            Start here for the biggest impact on your success and confidence.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {postsData?.posts.map((post) => {
            const readingTime = estimateReadingTime(post.content.rendered);
            
            return (
              <Card 
                key={post.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => handlePostClick(post)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <DocumentTextIcon className="h-5 w-5 text-primary mr-2" />
                      <span className="text-sm font-medium text-muted-foreground">
                        Guide
                      </span>
                    </div>
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                      <StarIcon className="h-3 w-3 mr-1" />
                      Popular
                    </Badge>
                  </div>
                  <CardTitle className="group-hover:text-primary transition-colors text-lg">
                    {docsService.stripHtml(post.title.rendered)}
                  </CardTitle>
                  <CardDescription>
                    {docsService.stripHtml(post.excerpt.rendered).substring(0, 150)}...
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {readingTime}
                    </div>
                    <div className="flex items-center">
                      <StarIcon className="h-4 w-4 mr-1 text-yellow-500" />
                      4.8
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-muted-foreground">
                      <UserGroupIcon className="h-3 w-3 mr-1" />
                      {new Date(post.date).toLocaleDateString()}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      All Audiences
                    </Badge>
                  </div>
                  
                  <div className="mt-4 pt-3 border-t">
                    <button className="text-sm font-medium text-primary hover:underline flex items-center">
                      Read Guide
                      <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <button 
            onClick={() => navigate('/support/all')}
            className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Browse All Resources
          </button>
        </div>
      </div>
    </section>
  );
};

export default PopularResources;